import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Modal, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Space, 
  Tooltip, 
  Select,
  Badge
} from 'antd';
import { 
  FaBug, 
  FaSearch, 
  FaSync, 
  FaFilter,
  FaRegTrashAlt, 
  FaEdit, 
  FaEye, 
  FaCheckCircle,
  FaUser,
  FaExclamationCircle,
  FaCheck,
  FaClock
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';
import AddBugModal from '../modal/add/AddBug';
import EditBugModal from '../modal/alter/AlterBug';
import ViewBugModal from '../modal/view/ViewBug';
import './Bugs.css';

const { Search } = Input;
const { Option } = Select;

const Bugs = () => {
  const [bugs, setBugs] = useState([]);
  const [filteredBugs, setFilteredBugs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [tipoFilter, setTipoFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);
  const [bugToDelete, setBugToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [uniqueTipos, setUniqueTipos] = useState([]);
  const [uniqueStatus, setUniqueStatus] = useState([]);
  const usuario = JSON.parse(localStorage.getItem('user'));

  // Estatísticas
  const totalBugs = useMemo(() => bugs.length, [bugs]);
  const bugsResolvidos = useMemo(() => 
    bugs.filter(bug => bug.status === 'RESOLVIDO' || bug.status === 'IMPLEMENTADO').length, 
    [bugs]
  );
  const bugsPendentes = useMemo(() => 
    bugs.filter(bug => bug.status === 'PENDENTE' || !bug.status).length, 
    [bugs]
  );

  const bugsPorTipo = useMemo(() => {
    const counts = {};
    bugs.forEach(bug => {
      const tipo = bug.tipo || 'NÃO DEFINIDO';
      counts[tipo] = (counts[tipo] || 0) + 1;
    });
    return counts;
  }, [bugs]);

  const sms_error = (sms) => {
    toast.error(sms, {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const sms_success = (sms) => {
    toast.success(sms, {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const fetchBugs = async () => {
    setLoading(true);
    try {
      const response = await Api.ListBug();
      if (response.success && Array.isArray(response.bugs)) {
        const formattedBugs = response.bugs.map((item, index) => ({
          key: item.id,
          id: item.id,
          descricao: item.descricao,
          tipo: item.tipo,
          parceiro: item.Nome,
          status: item.status || 'PENDENTE',
          id_user: item.id_user,
          numero: index + 1,
          dataRegistro: item.dataRegistro || new Date().toISOString().split('T')[0],
          criador: item.id_user === usuario.id ? 'Você' : 'Outro'
        }));
        
        setBugs(formattedBugs);
        setFilteredBugs(formattedBugs);
        
        // Extrair dados únicos para filtros
        const tipos = [...new Set(response.bugs
          .filter(item => item.tipo)
          .map(item => item.tipo))];
        setUniqueTipos(tipos);
        
        const status = [...new Set(response.bugs
          .filter(item => item.status)
          .map(item => item.status))];
        setUniqueStatus(status);
      } else {
        sms_error('Formato de resposta inválido');
      }
    } catch (error) {
      sms_error('Erro ao buscar bugs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBugs();
  }, []);

  // Função de busca global
  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      applyFilters({}, tipoFilter, statusFilter);
      return;
    }
    
    const filtered = bugs.filter(item =>
      item.parceiro?.toLowerCase().includes(value.toLowerCase()) ||
      item.descricao?.toLowerCase().includes(value.toLowerCase()) ||
      item.tipo?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredBugs(filtered);
  };

  // Aplicar todos os filtros
  const applyFilters = (filters, tipo, status) => {
    let filtered = [...bugs];
    
    // Aplicar filtros de campos
    Object.entries(filters).forEach(([key, val]) => {
      if (val) {
        filtered = filtered.filter(item => 
          item[key]?.toString().toLowerCase().includes(val.toLowerCase())
        );
      }
    });
    
    // Aplicar filtro de tipo
    if (tipo) {
      filtered = filtered.filter(item => 
        item.tipo?.toLowerCase() === tipo.toLowerCase()
      );
    }
    
    // Aplicar filtro de status
    if (status) {
      filtered = filtered.filter(item => 
        item.status?.toLowerCase() === status.toLowerCase()
      );
    }
    
    setFilteredBugs(filtered);
  };

  // Filtrar por campo específico
  const handleFilter = (field, value) => {
    const newFilters = { ...activeFilters, [field]: value };
    setActiveFilters(newFilters);
    applyFilters(newFilters, tipoFilter, statusFilter);
  };

  // Filtrar por tipo
  const handleTipoFilter = (value) => {
    setTipoFilter(value);
    applyFilters(activeFilters, value, statusFilter);
  };

  // Filtrar por status
  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    applyFilters(activeFilters, tipoFilter, value);
  };

  // Limpar filtros
  const clearFilters = () => {
    setActiveFilters({});
    setTipoFilter('');
    setStatusFilter('');
    setSearchText('');
    setFilteredBugs(bugs);
  };

  const handleAddBug = async (values) => {
    try {
      const costumerData = {
        descricao: values.descricao,
        tipo: values.tipo,
        id_user: usuario.id,
      };

      const result = await Api.AddBug(costumerData);
      if (result.success) {
        sms_success('Bug/Requisito adicionado com sucesso!');
        fetchBugs();
      } else {
        sms_error(result.message || 'Erro ao adicionar bug/requisito');
      }
    } catch (error) {
      sms_error('Erro ao adicionar bug/requisito: ' + error.message);
    }
  };

  const handleEditBug = async (values) => {
    try {
      const costumerData = {
        descricao: values.descricao,
        tipo: values.tipo,
        id: values.id,
      };

      const result = await Api.AlterBug(costumerData);
      if (result.success) {
        sms_success('Bug/Requisito editado com sucesso!');
        fetchBugs();
      } else {
        sms_error(result.message || 'Erro ao editar bug/requisito');
      }
    } catch (error) {
      sms_error('Erro ao editar bug/requisito: ' + error.message);
    }
  };

  const handleEditStatus = async (values) => {
    try {
      const status = values.tipo === 'Erro' ? 'RESOLVIDO' : 'IMPLEMENTADO';
      const costumerData = {
        status: status,
        id: values.key,
      };

      const result = await Api.AlterBugStatus(costumerData);
      if (result.success) {
        sms_success('Status atualizado com sucesso!');
        fetchBugs();
      } else {
        sms_error(result.message || 'Erro ao atualizar status');
      }
    } catch (error) {
      sms_error('Erro ao atualizar status: ' + error.message);
    }
  };

  const handleDeleteBug = async (id) => {
    try {
      const costumerData = {
        id: id,
      };

      const result = await Api.DeleteBug(costumerData);
      if (result.success) {
        sms_success('Bug/Requisito excluído com sucesso!');
        fetchBugs();
      } else {
        sms_error(result.message || 'Erro ao excluir bug/requisito');
      }
    } catch (error) {
      sms_error('Erro ao excluir bug/requisito: ' + error.message);
    }
  };

  const confirmDelete = (id) => {
    setBugToDelete(id);
    setIsDeleteModalOpen(true);
  };

  // Função para obter cor baseada no status
  const getStatusColor = (status) => {
    const colors = {
      'RESOLVIDO': 'green',
      'IMPLEMENTADO': 'blue',
      'PENDENTE': 'orange',
      'EM ANDAMENTO': 'cyan',
      '': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    if (status === 'RESOLVIDO' || status === 'IMPLEMENTADO') {
      return <FaCheck style={{ marginRight: 4 }} />;
    } else if (status === 'PENDENTE') {
      return <FaClock style={{ marginRight: 4 }} />;
    } else {
      return <FaExclamationCircle style={{ marginRight: 4 }} />;
    }
  };

  const columns = [
  
    {
      title: 'Parceiro',
      dataIndex: 'parceiro',
      key: 'parceiro',
      width: '20%',
      render: (text) => (
        <div className="partner-cell">
           
          <span className="partner-text">{text}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por parceiro"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              handleFilter('parceiro', selectedKeys[0]);
              confirm();
            }}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => {
                handleFilter('parceiro', selectedKeys[0]);
                confirm();
              }}
            >
              Filtrar
            </Button>
            <Button onClick={() => {
              clearFilters();
              handleFilter('parceiro', '');
            }}>
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      width: '35%',
      render: (text) => (
        <div className="description-cell">
           
          <span className="description-text" title={text}>
            {text.length > 100 ? text.substring(0, 100) + '...' : text}
          </span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por descrição"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              handleFilter('descricao', selectedKeys[0]);
              confirm();
            }}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => {
                handleFilter('descricao', selectedKeys[0]);
                confirm();
              }}
            >
              Filtrar
            </Button>
            <Button onClick={() => {
              clearFilters();
              handleFilter('descricao', '');
            }}>
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: '15%',
      align: 'center',
      render: (tipo) => (
        <Tag color={tipo === 'Erro' ? 'red' : 'purple'} className="tipo-tag">
          {tipo}
        </Tag>
      ),
      filters: uniqueTipos.map(tipo => ({ text: tipo, value: tipo })),
      onFilter: (value, record) => record.tipo === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      align: 'center',
      render: (status) => (
        <Tag color={getStatusColor(status)} className="status-tag">
          {getStatusIcon(status)}
          {status || 'PENDENTE'}
        </Tag>
      ),
      filters: uniqueStatus.map(status => ({ text: status, value: status })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: '15%',
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Visualizar detalhes">
            <Button
              type="default"
             
              icon={<FaEye />}
              size="small"
              onClick={() => {
                setSelectedBug(record);
                setViewModalOpen(true);
              }}
            />
          </Tooltip>
          
          {record.id_user === usuario.id && (
            <>
              <Tooltip title="Editar">
                <Button
                  type="primary"
                 
                  icon={<FaEdit />}
                  size="small"
                  onClick={() => {
                    setSelectedBug(record);
                    setEditModalOpen(true);
                  }}
                />
              </Tooltip>
              
              <Tooltip title="Excluir">
                <Button
                  type="primary"
                  danger
                 
                  icon={<FaRegTrashAlt />}
                  size="small"
                  onClick={() => confirmDelete(record.key)}
                />
              </Tooltip>
            </>
          )}
          
          {usuario.id === 1 && (
            <Tooltip title="Marcar como resolvido">
              <Button
                type="primary"
               
                icon={<FaCheckCircle />}
                size="small"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                onClick={() => handleEditStatus(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="bugs-container">
      <ToastContainer />
      
      {/* Cabeçalho */}
      <div className="page-header">
        <h1 className="page-title">
          <FaBug /> Gestão de Bugs/Requisitos
        </h1>
        <div className="header-actions">
          <Button 
            icon={<FaSync />} 
            onClick={fetchBugs}
            loading={loading}
          >
            Atualizar
          </Button>
          <Button
            type="primary"
             
            onClick={() => setAddModalOpen(true)}
          >
            Novo Bug/Requisito
          </Button>
        </div>
      </div>

      {/* Cards de estatísticas */}
      <Row gutter={16} className="stats-row">
        <Col xs={24} md={8}>
          <Card className="stat-card total-card">
            <Statistic
              title="Total de Registros"
              value={totalBugs}
              prefix={<FaBug />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div className="stat-trend">
              <span>Bugs e requisitos registrados</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="stat-card resolved-card">
            <Statistic
              title="Resolvidos/Implementados"
              value={bugsResolvidos}
              prefix={<FaCheck />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div className="stat-trend">
              <span>{totalBugs > 0 ? Math.round((bugsResolvidos / totalBugs) * 100) : 0}% do total</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="stat-card pending-card">
            <Statistic
              title="Pendentes"
              value={bugsPendentes}
              prefix={<FaClock />}
              valueStyle={{ color: '#faad14' }}
            />
            <div className="stat-trend">
              <span>Necessitam atenção</span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Distribuição por Tipo */}
      <Card className="distribution-card" style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaExclamationCircle /> Distribuição por Tipo
        </h3>
        <Row gutter={16}>
          {Object.entries(bugsPorTipo).map(([tipo, quantidade]) => (
            <Col xs={24} sm={12} md={6} key={tipo}>
              <div className="type-distribution">
                <div className="type-name">{tipo}</div>
                <div className="type-count">{quantidade}</div>
                <div className="type-progress">
                  <div className="progress-bar" style={{
                    width: `${(quantidade / totalBugs) * 100}%`,
                    backgroundColor: tipo === 'Erro' ? '#ff4d4f' : '#722ed1'
                  }}></div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Barra de ferramentas com filtros */}
      <Card className="toolbar-card">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12} lg={8}>
            <Search
              placeholder="Buscar por parceiro, descrição ou tipo..."
              prefix={<FaSearch />}
              enterButton="Buscar"
              size="large"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          
          <Col xs={24} md={12} lg={5}>
            <div className="filter-group">
              <label style={{ marginBottom: '4px', color: '#666' }}>
                <FaFilter style={{ marginRight: '4px' }} />
                Tipo:
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Todos os tipos"
                value={tipoFilter || undefined}
                onChange={handleTipoFilter}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="">Todos</Option>
                {uniqueTipos.map((tipo, index) => (
                  <Option key={index} value={tipo}>
                    {tipo}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} md={12} lg={5}>
            <div className="filter-group">
              <label style={{ marginBottom: '4px', color: '#666' }}>
                <FaCheck style={{ marginRight: '4px' }} />
                Status:
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Todos os status"
                value={statusFilter || undefined}
                onChange={handleStatusFilter}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="">Todos</Option>
                {uniqueStatus.map((status, index) => (
                  <Option key={index} value={status}>
                    {status}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} md={12} lg={6} style={{ textAlign: 'right' }}>
            <Button
              type="default"
              onClick={clearFilters}
              style={{ marginTop: '24px' }}
            >
              Limpar Filtros
            </Button>
          </Col>
        </Row>
        
        {/* Filtros ativos */}
        {(Object.keys(activeFilters).length > 0 || tipoFilter || statusFilter) && (
          <div className="active-filters">
            <span className="filters-label">Filtros ativos:</span>
            {Object.entries(activeFilters).map(([key, value]) => (
              value && (
                <Tag 
                  key={key} 
                  closable 
                  onClose={() => handleFilter(key, '')}
                  color="blue"
                >
                  {key === 'parceiro' ? 'Parceiro' : 
                   key === 'descricao' ? 'Descrição' : key}: {value}
                </Tag>
              )
            ))}
            {tipoFilter && (
              <Tag 
                closable 
                onClose={() => handleTipoFilter('')}
                color="purple"
              >
                Tipo: {tipoFilter}
              </Tag>
            )}
            {statusFilter && (
              <Tag 
                closable 
                onClose={() => handleStatusFilter('')}
                color="green"
              >
                Status: {statusFilter}
              </Tag>
            )}
          </div>
        )}
      </Card>

      {/* Tabela de bugs */}
      <div className="table-responsive-wrapper">
        <Card className="table-card">
          <Table
            columns={columns}
            dataSource={filteredBugs}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} de ${total} registros`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            rowClassName={(record, index) => 
              index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
            }
            locale={{
              emptyText: searchText || Object.keys(activeFilters).length > 0 || tipoFilter || statusFilter
                ? 'Nenhum bug/requisito encontrado com os filtros atuais' 
                : 'Nenhum bug/requisito cadastrado',
            }}
          />
        </Card>
      </div>

      {/* Modais */}
      <AddBugModal
        visible={isAddModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddBug}
      />
      <EditBugModal
        visible={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onEdit={handleEditBug}
        bug={selectedBug}
      />
      <ViewBugModal
        visible={isViewModalOpen}
        onClose={() => setViewModalOpen(false)}
        bug={selectedBug}
      />
      <Modal
        title="Confirmar Exclusão"
        visible={isDeleteModalOpen}
        onOk={() => {
          handleDeleteBug(bugToDelete);
          setIsDeleteModalOpen(false);
        }}
        onCancel={() => setIsDeleteModalOpen(false)}
        okText="Excluir"
        okButtonProps={{ danger: true }}
        cancelText="Cancelar"
        okType="danger"
      >
        <div className="confirm-modal-content">
          <div className="warning-icon">⚠️</div>
          <p>Tem certeza de que deseja excluir este bug/requisito?</p>
          <p className="delete-warning">Esta ação não pode ser desfeita.</p>
        </div>
      </Modal>
    </div>
  );
};

export default Bugs;